import React, { useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Select from "react-select";
import countryList from "react-select-country-list";
import { useForm } from "react-hook-form";
import { useRegisterUser } from "../api/auth";
import posthog from 'posthog-js';

function RegisterPage() {
  const {
    register,
    handleSubmit: validateSubmit,
    formState: { errors },
    setError,
    watch,
    getValues,
  } = useForm();
  const registerUser = useRegisterUser();
  const [serverError, setServerError] = useState("");
  const navigate = useNavigate();
  
  // Legacy redirect removed – team creation now happens after league join
  
  const customStyles = {
    control: (provided) => ({
      ...provided,
      backgroundColor: " #0E1020", // Цвет фона
      color: "#ffffff", // Цвет текста
      border: "1px solid #ffffff", // Цвет границы
      boxShadow: "none",
      height: "40px",
      borderRadius: "8px",
      "&:hover": {
        border: "1px solid #ffffff",
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      height: "40px",
      color: "#ffffff", // Цвет текста
    }),
    valueContainer: (provided) => ({
      // ...provided,
      height: "40px",
      lineHeight: "40px",
      fontSize: "14px",
      padding: "0 14px",
    }),
    input: (provided) => ({
      height: 40,
      pointerEvents: "none",
      position: "absolute",
      top: 0,
      left: 0,
      width: "200px",
      padding: "0 14px",
    }),

    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused ? "#4F7BFF" : " #0E1020", // Цвет фона при наведении
      color: state.isFocused ? "#ffffff" : "#ffffff", // Цвет текста
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: " #0E1020", // Цвет фона меню
      border: "1px solid #ffffff",
      borderRadius: "8px",
      overflow: "hidden",
      fontSize: "14px",
      zIndex: 20,
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#ffffff", // Цвет выбранного значения
      height: "40px",
    }),
  };

  const [image, setImage] = useState(null);
  const inputRef = useRef(null);

  const [value, setValue] = useState("");
  const options = useMemo(() => countryList().getData(), []);

  const changeHandler = (value) => {
    setValue(value);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleClearImage = () => {
    if (inputRef.current) {
      inputRef.current.value = "";
      setImage(null);
    }
  };

  const [startDate, setStartDate] = useState(null);

  // Add your form validation
  const onSubmit = (data) => {
    // Clear any previous server errors
    setServerError('');
    
    const userData = {
      firstName: data.firstName,
      lastName: data.lastName || '',
      username: data.username,
      email: data.email,
      password: data.password,
      ...(startDate ? { dateOfBirth: startDate } : {}),
      country: value?.label || '',
      
      // No team data at registration stage any more
    };

    if (image) {
      userData.avatar = image;
    }
    
    registerUser.mutateAsync(userData)
      .then(() => {
        // Success: Clear temporary team data and redirect
        localStorage.removeItem('pendingTeam');
        posthog.capture('user_registered', { username: userData.username, country: userData.country });
        // The mutation will handle navigation via react-query success callback
      })
      .catch(error => {
        console.error('Registration error in component:', error);
        
        // Handle structured validation errors (details array from Zod middleware)
        const details = error?.response?.data?.details;
        if (Array.isArray(details) && details.length > 0) {
          let hasAppliedFieldError = false;
          details.forEach(detail => {
            if (['firstName', 'lastName', 'username', 'email', 'password'].includes(detail.field)) {
              setError(detail.field, { type: 'server', message: detail.message });
              hasAppliedFieldError = true;
            }
          });

          if (hasAppliedFieldError) {
            return;
          }

          setServerError(`${details[0].field}: ${details[0].message}`);
          return;
        }

        // Extract error message from different possible error structures
        let errorMessage = "Registration failed. Please try again.";

        if (error?.response?.data?.error) {
          errorMessage = error.response.data.error;
        } else if (error?.message) {
          errorMessage = error.message;
        } else if (typeof error === 'string') {
          errorMessage = error;
        }

        // Set field-specific errors based on the error message
        if (errorMessage.includes('Email already in use') || errorMessage.includes('already registered')) {
          setError('email', { 
            type: 'server', 
            message: 'This email is already registered. Please use a different email or try logging in.' 
          });
        } else if (errorMessage.includes('Username already taken')) {
          setError('username', { 
            type: 'server', 
            message: 'This username is already taken. Please choose a different one.' 
          });
        } else if (errorMessage.includes('Password must be at least 8 characters')) {
          setError('password', { 
            type: 'server', 
            message: 'Password must be at least 8 characters long' 
          });
        } else if (errorMessage.includes('Password must contain at least one number')) {
          setError('password', { 
            type: 'server', 
            message: 'Password must contain at least one number' 
          });
        } else if (errorMessage.includes('Password must contain at least one special character')) {
          setError('password', { 
            type: 'server', 
            message: 'Password must contain at least one special character (!@#$%^&*(),.?":{}|<>)' 
          });
        } else if (errorMessage.includes('First Name') || errorMessage.includes('firstName')) {
          setError('firstName', { 
            type: 'server', 
            message: errorMessage 
          });
        } else if (errorMessage.includes('Too many registration attempts')) {
          setServerError('Too many registration attempts. Please wait an hour before trying again.');
        } else if (errorMessage.includes('Authentication required') || errorMessage.includes('Invalid token')) {
          setServerError('Session expired. Please refresh the page and try again.');
        } else {
          // Generic server error
          setServerError(errorMessage);
        }
        
        // Scroll to top to show error message
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
  };

  // Update your existing handleSubmit to use react-hook-form
  const handleSubmit = validateSubmit(onSubmit);

  return (
    <div className="page-login">
      <div className="wrapper">
        <h1>REGISTER</h1>

        <div className="steps">
          <span>1. PERSONAL INFORMATION</span>
        </div>

        <div className="form-block">
          <form onSubmit={handleSubmit}>
            {serverError && (
              <div className="error-message" style={{ 
                color: '#ff4444', 
                backgroundColor: 'rgba(255, 68, 68, 0.1)',
                border: '1px solid #ff4444',
                borderRadius: '8px',
                padding: '12px 16px',
                marginBottom: '1rem',
                fontSize: '14px',
                fontWeight: '500',
                textAlign: 'center'
              }}>
                {serverError}
              </div>
            )}
            <div className="form-group">
              <label htmlFor="i1">FIRST NAME *</label>

              <input
                id="firstName"
                type="text"
                className={errors.firstName ? 'error' : ''}
                style={errors.firstName ? { borderColor: '#ff4444', boxShadow: '0 0 5px rgba(255, 68, 68, 0.3)' } : {}}
                {...register("firstName", {
                  required: "First Name is required",
                  minLength: {
                    value: 2,
                    message: "First Name must be at least 2 characters",
                  },
                  pattern: {
                    value: /^[a-zA-Z\s]+$/,
                    message: "First Name can only contain letters and spaces"
                  }
                })}
              />
              {errors.firstName && (
                <div
                  style={{ color: "#ff4444", fontSize: "12px", marginTop: "4px", fontWeight: "500" }}
                >
                  {errors.firstName.message}
                </div>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="i2">LAST NAME</label>
              <input 
                id="lastName" 
                type="text" 
                className={errors.lastName ? 'error' : ''}
                style={errors.lastName ? { borderColor: '#ff4444', boxShadow: '0 0 5px rgba(255, 68, 68, 0.3)' } : {}}
                {...register("lastName")}
              />
              {errors.lastName && (
                <div
                  style={{ color: "#ff4444", fontSize: "12px", marginTop: "4px", fontWeight: "500" }}
                >
                  {errors.lastName.message}
                </div>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="username">USERNAME *</label>
              <input
                id="username"
                type="text"
                className={errors.username ? 'error' : ''}
                style={errors.username ? { borderColor: '#ff4444', boxShadow: '0 0 5px rgba(255, 68, 68, 0.3)' } : {}}
                {...register("username", {
                  required: "Username is required",
                  minLength: {
                    value: 3,
                    message: "Username must be at least 3 characters",
                  },
                  maxLength: {
                    value: 20,
                    message: "Username must be at most 20 characters",
                  },
                  pattern: {
                    value: /^[a-zA-Z0-9_-]+$/,
                    message: "Username can only contain letters, numbers, underscores and hyphens"
                  }
                })}
              />
              {errors.username && (
                <div
                  style={{ color: "#ff4444", fontSize: "12px", marginTop: "4px", fontWeight: "500" }}
                >
                  {errors.username.message}
                </div>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="i3">E-MAIL *</label>
              <input
                id="email"
                type="text"
                className={errors.email ? 'error' : ''}
                style={errors.email ? { borderColor: '#ff4444', boxShadow: '0 0 5px rgba(255, 68, 68, 0.3)' } : {}}
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Invalid email address"
                  }
                })}
              />
              {errors.email && (
                <div
                  style={{ color: "#ff4444", fontSize: "12px", marginTop: "4px", fontWeight: "500" }}
                >
                  {errors.email.message}
                </div>
              )}
            </div>
            <div className="form-group date-group">
              <label htmlFor="date_input">DATE OF BIRTH</label>
              <DatePicker
                id="date_input"
                selected={startDate}
                dateFormat="dd.MM.yyyy"
                onChange={(date) => setStartDate(date)}
                placeholderText="DD.MM.YYYY"
                showYearDropdown
                showMonthDropdown
                dropdownMode="select"
              />
              <div className="ico icon-calendar" />
            </div>
            <div className="form-group">
              <label htmlFor="s1">COUNTRY</label>
              <Select
                styles={customStyles}
                options={options}
                value={value}
                onChange={changeHandler}
              />
            </div>
            <div className="form-group">
              <label htmlFor="p1">PASSWORD</label>
              <input
                id="password"
                type="password"
                className={errors.password ? 'error' : ''}
                style={errors.password ? { borderColor: '#ff4444', boxShadow: '0 0 5px rgba(255, 68, 68, 0.3)' } : {}}
                {...register("password", {
                  required: "Password is required",
                  minLength: {
                    value: 8,
                    message: "Password must be at least 8 characters",
                  },
                  validate: {
                    hasNumber: (value) => 
                      /\d/.test(value) || "Password must contain at least one number",
                    hasSpecialChar: (value) => 
                      /[!@#$%^&*(),.?":{}|<>]/.test(value) || "Password must contain at least one special character"
                  }
                })}
              />
              {errors.password && (
                <div
                  style={{ color: "#ff4444", fontSize: "12px", marginTop: "4px", fontWeight: "500" }}
                >
                  {errors.password.message}
                </div>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="p2">CONFIRM PASSWORD *</label>
              <input
                id="confirmPassword"
                type="password"
                className={errors.confirmPassword ? 'error' : ''}
                style={errors.confirmPassword ? { borderColor: '#ff4444', boxShadow: '0 0 5px rgba(255, 68, 68, 0.3)' } : {}}
                {...register("confirmPassword", {
                  required: "Confirm Password is required",
                  validate: (value) =>
                    value === watch("password") || "Passwords must match",
                })}
              />
              {errors.confirmPassword && (
                <div
                  style={{ color: "#ff4444", fontSize: "12px", marginTop: "4px", fontWeight: "500" }}
                >
                  {errors.confirmPassword.message}
                </div>
              )}
            </div>

            <div className="form-group photo-group">
              <div className="photo-grid">
                <div className="upload-block">
                  <h4>UPLOAD AVATAR</h4>
                  <label htmlFor="photo_input" className="read-more-btn">
                    CHOOSE FILE
                  </label>
                  <input
                    ref={inputRef}
                    id="photo_input"
                    accept="image/*"
                    type="file"
                    onChange={handleImageChange}
                  />
                </div>
                <div className="file-tab">
                  <div className={`tab file-block ${image ? "active" : ""}`}>
                    <div className="image">
                      <div
                        className="image-clear-btn"
                        onClick={() => {
                          handleClearImage();
                        }}
                      />
                      <img src={image} id="file_preview" alt="Preview" />
                    </div>
                  </div>
                  <div className="tab default-block" />
                </div>
              </div>
            </div>

            <button 
              className="submit-btn" 
              type="submit"
              disabled={registerUser.isPending}
            >
              {registerUser.isPending ? 'REGISTERING...' : 'CONTINUE'}
            </button>
          </form>

          <div className="descr">* Required field</div>
        </div>
      </div>
    </div>
  );
}

export { RegisterPage };
